/**
 * Documents entered by hand (1.59, decisions of 10/10/2026): only in the
 * ciclo passivo. An invoice that never reaches Gestify (rare: a private person
 * issuing it) or a pro forma, a debt like the others, collected in the
 * scadenzario and paid through the bank. When the invoices of Gestify come,
 * they replace the pro forma: what was paid on it passes to them, the oldest
 * invoice first, and the pro forma leaves the totals. The invoices rule: a
 * difference with the pro forma is shown, never corrected.
 */
import { z } from 'zod';
import { parseIsoDate } from './dates.js';
import { fromCents, toCents } from './money.js';
import { idSchema, isoDateSchema, optionalText } from './validation.js';
/** The kinds a person may enter by hand. */
export const MANUAL_INVOICE_KINDS = ['fattura', 'proforma'];
/** The key of a document entered by hand, in place of a code of Gestify. */
export const MANUAL_KEY_PREFIX = 'MAN-';
const amount = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
    .transform((value) => fromCents(toCents(value)));
export const manualInvoiceSchema = z
    .object({
    kind: z.enum(MANUAL_INVOICE_KINDS),
    subjectId: idSchema,
    number: z.string().trim().min(1, { message: 'Numero obbligatorio' }).max(60),
    date: isoDateSchema,
    taxable: amount,
    vat: amount,
    /** Withheld by K-Rev and paid to the tax office with the F24: not paid to the supplier. */
    withholding: amount,
    paymentTerms: optionalText(200),
    notes: optionalText(2000),
    /** Empty: the whole amount due on the date of the document. */
    dues: z.array(z.object({ dueDate: isoDateSchema, amount })).max(60),
})
    .superRefine((value, context) => {
    const { netDue } = manualTotals(value);
    if (toCents(netDue) <= 0) {
        context.addIssue({
            code: 'custom',
            path: ['taxable'],
            message: 'Il netto da pagare deve essere maggiore di zero',
        });
    }
    if (value.dues.length) {
        const sum = value.dues.reduce((total, due) => total + toCents(due.amount), 0);
        if (sum !== toCents(netDue)) {
            context.addIssue({
                code: 'custom',
                path: ['dues'],
                message: `Le scadenze sommano ${fromCents(sum)}, il netto da pagare è ${netDue}`,
            });
        }
    }
});
/** Total and net due of a document entered by hand: taxable + VAT, less the withholding. */
export function manualTotals(amounts) {
    const total = toCents(amounts.taxable) + toCents(amounts.vat);
    return { total: fromCents(total), netDue: fromCents(total - toCents(amounts.withholding)) };
}
/** Same supplier, same number written in any way, same date: probably the same document. */
export function sameDocument(a, b) {
    const key = (value) => value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    return (a.subjectId !== null &&
        a.subjectId === b.subjectId &&
        a.date === b.date &&
        key(a.number) === key(b.number));
}
/* --- Pro forma replaced by their invoices ---------------------------------------- */
/** The invoices of Gestify that replace a pro forma, and where each payment on it goes. */
export const proformaReplaceSchema = z.object({
    invoiceIds: z
        .array(idSchema)
        .min(1, { message: 'Scegli almeno una fattura' })
        .max(10)
        .refine((ids) => new Set(ids).size === ids.length, {
        message: 'Una fattura compare due volte',
    }),
    /** Each payment still on the pro forma, with the invoice it passes to. */
    settlements: z.array(z.object({ settlementId: idSchema, invoiceId: idSchema })).max(200),
});
/** How far before the pro forma an invoice may be dated and still replace it. */
export const PROFORMA_INVOICE_DAYS_BEFORE = 7;
/**
 * The invoices of a supplier that may replace a pro forma: invoices of Gestify
 * from a week before it on, those with the same amount (within 10 %) first,
 * then by date from the nearest.
 */
export function proformaCandidates(proforma, invoices) {
    const net = Math.abs(toCents(proforma.netDue));
    const start = parseIsoDate(proforma.date).getTime() - PROFORMA_INVOICE_DAYS_BEFORE * 86_400_000;
    return invoices
        .filter((invoice) => parseIsoDate(invoice.date).getTime() >= start)
        .map((invoice) => ({
        ...invoice,
        likely: Math.abs(Math.abs(toCents(invoice.netDue)) - net) <= net / 10,
    }))
        .sort((a, b) => Number(b.likely) - Number(a.likely) ||
        Math.abs(parseIsoDate(a.date).getTime() - parseIsoDate(proforma.date).getTime()) -
            Math.abs(parseIsoDate(b.date).getTime() - parseIsoDate(proforma.date).getTime()) ||
        a.number.localeCompare(b.number, 'it'));
}
/**
 * Where the payments of a pro forma go among its invoices (decision of
 * 10/10/2026: the oldest invoice first). A payment is never split: it goes to
 * the oldest invoice it still fits in, else to the oldest with something
 * left, else to the newest.
 */
export function proformaAllocation(settlements, invoices) {
    if (!invoices.length)
        return [];
    const ordered = [...invoices].sort((a, b) => a.date.localeCompare(b.date) || a.number.localeCompare(b.number, 'it'));
    const room = new Map(ordered.map((invoice) => [invoice.id, toCents(invoice.netDue)]));
    return [...settlements]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((settlement) => {
        const cents = toCents(settlement.amount);
        const target = ordered.find((invoice) => room.get(invoice.id) >= cents) ??
            ordered.find((invoice) => room.get(invoice.id) > 0) ??
            ordered[ordered.length - 1];
        room.set(target.id, room.get(target.id) - cents);
        return { settlementId: settlement.id, invoiceId: target.id };
    });
}
/** The invoices less the pro forma, or null when they are the same. */
export function proformaDifference(proformaNet, invoices) {
    const cents = invoices.reduce((sum, invoice) => sum + toCents(invoice.netDue), 0) - toCents(proformaNet);
    return cents === 0 ? null : fromCents(cents);
}
/** The PDF of a document entered by hand, uploaded in the folder of the supplier; null forgets it. */
export const invoiceAttachmentSchema = z.object({
    attachment: z
        .object({
        itemId: z.string().trim().min(1).max(200),
        name: z.string().trim().min(1).max(300),
    })
        .nullable(),
});
//# sourceMappingURL=manual-invoices.js.map